import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

import { XoneraSpinner } from '@/components/core/common/feedback/XoneraSpinner';
import { AuthCard, AuthLayout } from '@/components/features/auth/AuthLayout';
import { SetNewPasswordCard } from '@/components/features/auth/SetNewPasswordCard';
import {
  AuthStatusButton,
  AuthStatusCard,
  AuthStatusLink,
} from '@/components/features/auth/status';
import { supabase } from '@/config/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { notify } from '@/lib/notify';
import type { NewPasswordForm } from '@/lib/schemas/auth';

type RecoveryStatus = 'checking' | 'ready' | 'invalid';

/**
 * Waits for the recovery session Supabase establishes from the emailed link.
 *
 * The `PASSWORD_RECOVERY` event may fire before or after this mounts, so it
 * both subscribes AND checks the session that `detectSessionInUrl` has likely
 * already set. If neither shows up in 4s the link was missing or consumed.
 */
function useRecoverySession(isDevPreview: boolean) {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<RecoveryStatus>(
    isDevPreview ? 'ready' : 'checking'
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isDevPreview) return;
    let settled = false;

    const errorCode = searchParams.get('error_code');
    if (errorCode) {
      settled = true;
      setErrorMessage(
        searchParams.get('error_description') ||
          'This password reset link is invalid or expired.'
      );
      setStatus('invalid');
      return;
    }

    const markReady = () => {
      if (settled) return;
      settled = true;
      setStatus('ready');
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        markReady();
      }
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) markReady();
    });

    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      setErrorMessage(
        'This password reset link is invalid or has already been used.'
      );
      setStatus('invalid');
    }, 4000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [searchParams, isDevPreview]);

  return { status, errorMessage };
}

/**
 * Landing page for the Supabase password-recovery link.
 *
 * The recovery email links here carrying a one-time token. The Supabase client
 * (configured with `detectSessionInUrl: true`) consumes it on load and
 * establishes a temporary session, emitting `PASSWORD_RECOVERY`. This page waits
 * for that session, then sets a new password via `updatePassword`.
 *
 * Mounted BARE (not under `PublicRoute`) so the recovery session - which sets
 * `user` - does not bounce back to the login page before the reset finishes.
 */
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updatePassword } = useAuth();

  // Dev-only UI preview: `/auth/reset-password?preview=1` renders the form
  // without a recovery session. `import.meta.env.DEV` is false in production
  // builds, so this is stripped.
  const isDevPreview =
    import.meta.env.DEV && searchParams.get('preview') === '1';

  const { status, errorMessage } = useRecoverySession(isDevPreview);

  const handleSubmit = async (values: NewPasswordForm) => {
    const { error } = await updatePassword(values.password);
    if (error) {
      notify.error(error, 'Failed to update password');
      return;
    }
    notify.success('Password updated. You are now signed in.');
    navigate('/', { replace: true });
  };

  return (
    <AuthLayout>
      <AuthCard>
        {status === 'invalid' && (
          <AuthStatusCard
            tone='error'
            icon={<AlertTriangle className='h-5 w-5' />}
            title='That link has expired'
            body={
              errorMessage ||
              'Links are single-use and time-limited. Send yourself a fresh one and it will work.'
            }
          >
            <AuthStatusButton
              onClick={() => navigate('/auth?mode=reset', { replace: true })}
            >
              Send a new link
            </AuthStatusButton>
            <AuthStatusLink
              onClick={() => navigate('/auth?mode=login', { replace: true })}
            >
              Back to sign in
            </AuthStatusLink>
          </AuthStatusCard>
        )}

        {status === 'checking' && (
          <AuthStatusCard
            tone='info'
            icon={<XoneraSpinner className='h-5 w-5' label='' />}
            title='Verifying your link'
            body='One moment while we confirm your password reset request.'
          />
        )}

        {status === 'ready' && (
          <SetNewPasswordCard
            onSubmit={handleSubmit}
            onBackToSignIn={() =>
              navigate('/auth?mode=login', { replace: true })
            }
          />
        )}
      </AuthCard>
    </AuthLayout>
  );
}
